import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { ease, viewportOnce, stagger, staggerItem } from '../motion';
import { aboutBio, highlights, stats } from '../content';
import SectionHeading from './SectionHeading';
import Counter from './Counter';

const About: React.FC = () => (
  <Box component="section" id="about" sx={{ py: { xs: 10, md: 18 }, position: 'relative' }}>
    <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
      <SectionHeading index="01" eyebrow="about" title="A bit about me" />

      {/* Bio + stats two-column */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
          gap: { xs: 6, md: 10 },
          alignItems: 'start',
          mb: { xs: 8, md: 12 },
        }}
      >
        <Box>
          {aboutBio.map((para, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.6, delay: i * 0.1, ease: ease.out }}
            >
              <Typography
                variant="body1"
                sx={{ color: i === 0 ? 'text.primary' : 'text.secondary', mb: 3, fontSize: { md: '1.15rem' } }}
              >
                {para}
              </Typography>
            </motion.div>
          ))}
        </Box>

        {/* Stats */}
        <motion.div
          variants={stagger(0.08)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 0,
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {stats.map((stat, i) => (
              <motion.div key={i} variants={staggerItem}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRight: i % 2 === 0 ? (t) => `1px solid ${t.palette.divider}` : 'none',
                    borderBottom: i < 2 ? (t) => `1px solid ${t.palette.divider}` : 'none',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{ color: 'primary.main', mb: 0.5, fontSize: { xs: '2rem', md: '2.4rem' } }}
                  >
                    <Counter value={stat.value} suffix={stat.suffix} />
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: (t) => t.typography.button.fontFamily,
                      fontSize: '0.72rem',
                      letterSpacing: '0.04em',
                      color: 'text.secondary',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>

      {/* Highlight tiles */}
      <motion.div
        variants={stagger(0.1)}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {highlights.map((h, i) => (
            <motion.div key={i} variants={staggerItem}>
              <Box
                sx={{
                  height: '100%',
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  backgroundColor: 'background.paper',
                  transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1), border-color 0.35s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: (t) => `${t.palette.primary.main}55`,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: (t) => t.typography.button.fontFamily,
                    color: 'primary.main',
                    fontSize: '0.8rem',
                    mb: 2,
                  }}
                >
                  0{i + 1}
                </Typography>
                <Typography variant="h4" sx={{ mb: 1.5, color: 'text.primary' }}>
                  {h.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {h.description}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </Container>
  </Box>
);

export default About;
